/* ==========================================================================
   tl-ladies.js — Tokyo Lifestyle · Ladies Three Piece landing
   --------------------------------------------------------------------------
   Runs the bag, the EN/বাংলা switch, the theme switch, and the motion system
   on /pages/three-piece-offer.

   COMPATIBILITY CONTRACT
   This page's traffic is overwhelmingly Android inside the Facebook and
   Instagram in-app browsers, which run an older Chromium than the phone's real
   browser. The code therefore stays inside ES2015 on purpose: no optional
   chaining, no nullish coalescing, no object spread, no Array.prototype.flat,
   no Object.assign on DOM objects. IntersectionObserver, ResizeObserver and
   fetch are all feature-detected, and every one of them degrades to a working
   page rather than a broken one.

   PRICING CONTRACT
   Nothing here computes a discount. Every `now` price was calculated in Liquid
   from the variant's real price and shipped in the data island, so the
   server-rendered card and the scripted bag line can never disagree by a
   rounding taka. The 10% itself is applied by Shopify, because every checkout
   this file builds carries `?discount=<code>` on the cart permalink. Verified
   against a live cart on 2 Aug 2026 — see the section file's header.

   MOTION CONTRACT
   One requestAnimationFrame ticker drives everything. Not one per animation —
   a dozen independent rAF loops on a mid-range Android is exactly how these
   pages start dropping frames. Only `transform` and `opacity` are ever
   animated, so no animation in this file can trigger layout. Anything
   scroll-linked reads `pageYOffset` and cached rects; nothing calls
   getBoundingClientRect inside the frame loop.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('tll-root');
  var dataEl = document.getElementById('tll-data');
  if (!root || !dataEl) return;

  var DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  var PCT = DATA.pct || 10;
  var CODE = DATA.code || '';
  var KEEP = (100 - PCT) / 100;
  var DELIVERY = DATA.delivery || { inside: 70, outside: 130 };
  var TOTAL = DATA.total || 0;
  var FROM_NOW = DATA.fromNow || 0;
  var FROM_WAS = DATA.fromWas || 0;
  var HERO = DATA.hero || [];
  var HERO_MS = (DATA.heroRotateSec || 3) * 1000;

  var reduceMotion =
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Product cache, keyed by handle, filled from /products/<handle>.js.
     There is deliberately no product array in the data island: the island sits
     outside {% paginate %} and would always describe page 1. Fetching per
     product is also the only way to get a truthful stock count on a catalogue
     averaging 1.27 units per design. */
  var CACHE = {};

  /* Must match the section's FILENAME — that is the id Shopify's Section
     Rendering API expects for a section included with {% section %}. */
  var SECTION_ID = 'tl-ladies-landing';

  /* Discount maths, in integer paisa, mirroring exactly what the Liquid does:
       {{ price_cents | times: keep | divided_by: 100 | divided_by: 100 }}
     Liquid's divided_by truncates on integers, so Math.floor is required — with
     Math.round a ৳999 item would print ৳899 server-side and ৳899.1→899 here,
     but a ৳995 item would print 895 vs 896. The two must never disagree. */
  function discounted(cents) {
    return Math.floor(Math.floor((cents * (100 - PCT)) / 100) / 100);
  }

  /* ======================================================================
     1. MOTION ENGINE
     ----------------------------------------------------------------------
     A small Framer-Motion-shaped layer, written for this page's constraints.
     Public surface: Ticker.add/remove, Spring, reveal(), parallax(),
     magnetic(), counter(), flip().
     ====================================================================== */

  /* ── 1a. The single ticker ───────────────────────────────────────────
     Every animated thing registers a step(dt) function here. The loop stops
     itself entirely when nothing is registered, so an idle page costs zero
     frames — important, because this page can sit open for minutes while
     someone reads the FAQ. */
  var Ticker = (function () {
    var subs = [];
    var running = false;
    var last = 0;

    function frame(now) {
      if (!subs.length) {
        running = false;
        return;
      }
      /* Clamp dt. A backgrounded tab resumes with a multi-second delta, and an
         unclamped spring integrated over 4000ms explodes to infinity. */
      var dt = last ? Math.min(now - last, 48) : 16;
      last = now;

      /* Iterate a copy: a step function is allowed to remove itself. */
      var list = subs.slice();
      for (var i = 0; i < list.length; i++) {
        if (list[i].done) continue;
        list[i].step(dt);
      }
      for (var j = subs.length - 1; j >= 0; j--) {
        if (subs[j].done) subs.splice(j, 1);
      }
      window.requestAnimationFrame(frame);
    }

    return {
      add: function (step) {
        var sub = { step: step, done: false };
        subs.push(sub);
        if (!running) {
          running = true;
          last = 0;
          window.requestAnimationFrame(frame);
        }
        return sub;
      },
      remove: function (sub) {
        if (sub) sub.done = true;
      },
    };
  })();

  /* ── 1b. Spring ──────────────────────────────────────────────────────
     Semi-implicit Euler on a damped harmonic oscillator — the same model
     Framer Motion uses. Velocity carries across retargets, which is what
     makes an interrupted spring feel physical instead of restarting.

     Integrated at a fixed 1/120s substep regardless of frame rate, so a 60Hz
     phone and a 120Hz tablet settle identically. Variable-dt integration
     makes stiffness frame-rate dependent, which is a genuinely nasty bug to
     chase down later. */
  function Spring(value, opts) {
    opts = opts || {};
    this.stiffness = opts.stiffness || 170;
    this.damping = opts.damping || 26;
    this.mass = opts.mass || 1;
    this.precision = opts.precision || 0.01;
    this.value = value || 0;
    this.target = this.value;
    this.velocity = 0;
    this.resting = true;
  }

  Spring.prototype.set = function (target) {
    if (target === this.target) return;
    this.target = target;
    this.resting = false;
  };

  Spring.prototype.jump = function (value) {
    this.value = this.target = value;
    this.velocity = 0;
    this.resting = true;
  };

  Spring.prototype.step = function (dtMs) {
    if (this.resting) return this.value;
    var steps = Math.max(1, Math.min(8, Math.round(dtMs / 8.333)));
    var h = dtMs / steps / 1000;
    for (var i = 0; i < steps; i++) {
      var f = -this.stiffness * (this.value - this.target);
      var d = -this.damping * this.velocity;
      var a = (f + d) / this.mass;
      this.velocity += a * h;
      this.value += this.velocity * h;
    }
    if (
      Math.abs(this.value - this.target) < this.precision &&
      Math.abs(this.velocity) < this.precision
    ) {
      this.value = this.target;
      this.velocity = 0;
      this.resting = true;
    }
    return this.value;
  };

  /* ── 1c. Reveal, with stagger ────────────────────────────────────────
     Elements rest at opacity 0 in CSS and are revealed by flipping data-in.
     Siblings that share a parent and carry data-rv-stagger get an increasing
     delay, so a row of cards cascades instead of appearing as a block.

     Without IntersectionObserver everything is revealed immediately — the
     page must never depend on the observer to be readable. */
  function reveal() {
    var items = root.querySelectorAll('[data-rv]');
    var i;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (i = 0; i < items.length; i++) items[i].setAttribute('data-in', 'true');
      return;
    }

    /* Assign stagger delays per parent, once, up front. */
    var counters = [];
    for (i = 0; i < items.length; i++) {
      var el = items[i];
      if (!el.hasAttribute('data-rv-stagger')) continue;
      var parent = el.parentNode;
      var idx = -1;
      for (var c = 0; c < counters.length; c++) {
        if (counters[c].node === parent) idx = c;
      }
      if (idx === -1) {
        counters.push({ node: parent, n: 0 });
        idx = counters.length - 1;
      }
      /* Cap the cascade. Card 20 of a 24-card grid must not wait 1.4s. */
      var slot = Math.min(counters[idx].n, 7);
      el.style.setProperty('--rv-d', slot * 65 + 'ms');
      counters[idx].n++;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (!entries[k].isIntersecting) continue;
          entries[k].target.setAttribute('data-in', 'true');
          io.unobserve(entries[k].target);
        }
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );
    for (i = 0; i < items.length; i++) io.observe(items[i]);
  }

  /* ── 1d. Scroll-linked parallax ──────────────────────────────────────
     Registered against the shared ticker, but only while the element is on
     screen. The scroll listener itself writes a single number; all the maths
     happens in the frame callback, and the element's offset is measured on
     resize rather than per frame. */
  function parallax() {
    if (reduceMotion) return;
    var nodes = root.querySelectorAll('[data-parallax]');
    if (!nodes.length || !('IntersectionObserver' in window)) return;

    var items = [];
    var n;
    for (n = 0; n < nodes.length; n++) {
      items.push({
        el: nodes[n],
        rate: parseFloat(nodes[n].getAttribute('data-parallax')) || 0.06,
        top: 0,
        h: 0,
        visible: false,
        spring: new Spring(0, { stiffness: 120, damping: 24 }),
      });
    }

    function measure() {
      var sy = window.pageYOffset || document.documentElement.scrollTop;
      for (var m = 0; m < items.length; m++) {
        var r = items[m].el.getBoundingClientRect();
        items[m].top = r.top + sy;
        items[m].h = r.height;
      }
    }

    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        for (var m = 0; m < items.length; m++) {
          if (items[m].el === entries[e].target) items[m].visible = entries[e].isIntersecting;
        }
      }
    });
    for (n = 0; n < items.length; n++) io.observe(items[n].el);

    measure();
    window.addEventListener('resize', measure);
    if ('ResizeObserver' in window) {
      /* Images finishing decode changes the hero's height after first paint;
         without this the parallax anchor is measured against a collapsed box. */
      var ro = new ResizeObserver(measure);
      ro.observe(root);
    }

    Ticker.add(function (dt) {
      var sy = window.pageYOffset || document.documentElement.scrollTop;
      var vh = window.innerHeight || 800;
      for (var m = 0; m < items.length; m++) {
        var it = items[m];
        if (!it.visible) continue;
        /* -1 above the fold … +1 below it. */
        var centre = it.top + it.h / 2 - (sy + vh / 2);
        it.spring.set(-centre * it.rate);
        var v = it.spring.step(dt);
        it.el.style.transform = 'translate3d(0,' + v.toFixed(2) + 'px,0)';
      }
    });
  }

  /* ── 1e. Magnetic buttons ────────────────────────────────────────────
     Pointer-driven attraction, spring-damped. Bound only under a real hover
     pointer: on touch there is no cursor to attract toward, and binding it
     anyway costs listeners for nothing. */
  function magnetic() {
    if (reduceMotion) return;
    if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) {
      return;
    }
    var nodes = root.querySelectorAll('[data-magnetic]');

    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        var sx = new Spring(0, { stiffness: 210, damping: 20 });
        var sy = new Spring(0, { stiffness: 210, damping: 20 });
        var sub = null;
        var rect = null;

        function stop() {
          if (sub) {
            Ticker.remove(sub);
            sub = null;
          }
        }

        function start() {
          if (sub) return;
          sub = Ticker.add(function (dt) {
            var x = sx.step(dt);
            var y = sy.step(dt);
            el.style.translate = x.toFixed(2) + 'px ' + y.toFixed(2) + 'px';
            if (sx.resting && sy.resting) {
              if (Math.abs(x) < 0.02 && Math.abs(y) < 0.02) el.style.translate = '';
              stop();
            }
          });
        }

        el.addEventListener('pointerenter', function () {
          rect = el.getBoundingClientRect();
        });

        el.addEventListener('pointermove', function (ev) {
          if (!rect) rect = el.getBoundingClientRect();
          var dx = ev.clientX - (rect.left + rect.width / 2);
          var dy = ev.clientY - (rect.top + rect.height / 2);
          /* Cap the pull so a wide button cannot slide out from under the
             cursor, which makes it feel slippery rather than responsive. */
          sx.set(Math.max(-10, Math.min(10, dx * 0.22)));
          sy.set(Math.max(-8, Math.min(8, dy * 0.28)));
          start();
        });

        el.addEventListener('pointerleave', function () {
          rect = null;
          sx.set(0);
          sy.set(0);
          start();
        });
      })(nodes[i]);
    }
  }

  /* ── 1e2. Hero rotation ──────────────────────────────────────────────
     The hero rail shows REAL products, not a composed image, for one reason:
     this catalogue averages 1.27 units per design, so anything baked into a
     picture is advertising something that may already be gone. The pool is
     rendered server-side from `available` products only — a sold-out design
     can never enter it, and one that restocks reappears on the next render.

     One panel changes at a time. Swapping all four at once reads as a page
     glitch; swapping one reads as a rail of stock moving. */
  function heroRotate() {
    var cells = root.querySelectorAll('[data-hero-cell]');
    if (!cells.length || HERO.length <= cells.length || reduceMotion) return;

    var cursor = cells.length;      /* first product not already on screen */
    var slot = 0;
    var timer = null;
    var live = true;

    function swap() {
      var cell = cells[slot % cells.length];
      var item = HERO[cursor % HERO.length];
      slot++;
      cursor++;
      if (!cell || !item) return;

      var img = cell.querySelector('img');
      var now = cell.querySelector('[data-cell-now]');
      var was = cell.querySelector('[data-cell-was]');

      /* Preload before revealing. Swapping src on a visible <img> shows a
         blank frame on a slow connection, which is worse than not rotating. */
      var pre = new Image();
      pre.onload = function () {
        cell.setAttribute('data-swap', 'true');
        window.setTimeout(function () {
          if (img) {
            img.removeAttribute('srcset');
            img.setAttribute('src', item.img);
            img.setAttribute('alt', '');
          }
          if (now) now.textContent = '৳' + digits(item.now);
          if (was) was.textContent = '৳' + digits(item.was);
          cell.setAttribute('data-handle', item.h);
          cell.setAttribute('data-swap', 'false');
        }, 260);
      };
      pre.src = item.img;
    }

    function start() {
      if (timer || !live) return;
      timer = window.setInterval(swap, HERO_MS);
    }
    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          live = entries[0].isIntersecting;
          if (live) start();
          else stop();
        },
        { threshold: 0.1 }
      );
      io.observe(cells[0].parentNode);
    } else {
      start();
    }

    /* A backgrounded tab should not queue up swaps to fire all at once. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else if (live) start();
    });
  }

  /* ── 1f. Number counter ──────────────────────────────────────────────── */
  function counters() {
    var nodes = root.querySelectorAll('[data-count-to]');
    if (!nodes.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (var q = 0; q < nodes.length; q++) {
        nodes[q].textContent = digits(nodes[q].getAttribute('data-count-to'));
      }
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (!entries[e].isIntersecting) continue;
          var el = entries[e].target;
          io.unobserve(el);
          run(el, parseInt(el.getAttribute('data-count-to'), 10) || 0);
        }
      },
      { threshold: 0.4 }
    );
    for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);

    function run(el, to) {
      var from = 0;
      var t = 0;
      var dur = 900;
      var sub = Ticker.add(function (dt) {
        t += dt;
        var p = Math.min(1, t / dur);
        /* easeOutExpo — fast commitment, long settle. Reads as "counting up
           and landing" rather than a linear ramp. */
        var e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        el.textContent = digits(Math.round(from + (to - from) * e));
        if (p >= 1) Ticker.remove(sub);
      });
    }
  }

  /* ── 1g. FLIP ────────────────────────────────────────────────────────
     First / Last / Invert / Play. Used when the grid is filtered or re-sorted
     so cards glide to their new positions instead of teleporting.

     Reads all positions before the mutation and all positions after, in two
     tight batches — never interleaved with writes, which would thrash layout
     once per card. */
  function flip(container, mutate) {
    var kids = container.children;
    var i;

    if (reduceMotion) {
      mutate();
      return;
    }

    var first = [];
    for (i = 0; i < kids.length; i++) {
      var r = kids[i].getBoundingClientRect();
      first.push({ el: kids[i], x: r.left, y: r.top, w: r.width, shown: r.width > 0 });
    }

    mutate();

    var moves = [];
    for (i = 0; i < first.length; i++) {
      var rec = first[i];
      var rc = rec.el.getBoundingClientRect();
      var nowShown = rc.width > 0;
      if (!rec.shown || !nowShown) continue;
      var dx = rec.x - rc.left;
      var dy = rec.y - rc.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      moves.push({ el: rec.el, dx: dx, dy: dy });
    }
    if (!moves.length) return;

    for (i = 0; i < moves.length; i++) {
      var mv = moves[i];
      mv.el.style.transition = 'none';
      mv.el.style.transform = 'translate3d(' + mv.dx + 'px,' + mv.dy + 'px,0)';
    }

    /* Two frames: one to flush the inverted position, one to release it.
       A single rAF is occasionally coalesced with the style write above and
       the transition never starts. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        for (var k = 0; k < moves.length; k++) {
          var el = moves[k].el;
          el.style.transition = 'transform 460ms cubic-bezier(0.22,0.68,0.24,1)';
          el.style.transform = '';
        }
        window.setTimeout(function () {
          for (var k2 = 0; k2 < moves.length; k2++) {
            moves[k2].el.style.transition = '';
          }
        }, 500);
      });
    });
  }

  /* ======================================================================
     2. COPY
     ----------------------------------------------------------------------
     Bengali lives here; English is scraped out of the server-rendered markup
     by readEnglish(), so it is never written twice and the two can never
     drift. Values may contain {n} / {p} placeholders and, for data-i18n-html
     keys, inline markup.
     ====================================================================== */

  /* BN_D and digitsRaw MUST be defined before the BN literal below, which
     calls digitsRaw() while it is being constructed. `function digitsRaw` is
     hoisted, but `var BN_D = [...]` is NOT — its assignment runs in source
     order. With this block further down, digitsRaw read an undefined BN_D and
     the whole IIFE threw "Cannot read properties of undefined (reading '1')"
     before a single line of the page was wired up: no reveals, no dock, no
     bag. The page still LOOKED fine in HTML, which is what made it worth a
     comment — the failure was invisible without opening the console. */
  var BN_D = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

  function digitsRaw(v) {
    /* Used while building the BN dictionary, before `lang` exists — always
       converts, because the string it lands in is Bengali by definition. */
    return String(v).replace(/[0-9]/g, function (d) {
      return BN_D[+d];
    });
  }

  var BN = {
    'nav.offer': 'অফার',
    'nav.collection': 'কালেকশন',
    'nav.sizing': 'সাইজ',
    'nav.faq': 'প্রশ্ন',
    'nav.themeLabel': 'থিম বদলান',

    'tape.off': 'প্রতিটি থ্রি পিসে ফ্ল্যাট ' + digitsRaw(PCT) + '% ছাড়',
    'tape.cod': 'সারা বাংলাদেশে ক্যাশ অন ডেলিভারি',
    'tape.stock': 'বেশিরভাগ ডিজাইনই মাত্র একটি করে',
    'tape.from': 'ছাড়ের পর শুরু মাত্র ৳' + digitsRaw(FROM_NOW),

    'hero.chip': 'শুধু এই পেজের জন্য',
    'hero.title1': 'ফ্ল্যাট ' + digitsRaw(PCT) + '% ছাড়',
    'hero.title2': 'প্রতিটি থ্রি পিসে।',
    'hero.tape':
      'শুরু <b>৳' + digitsRaw(FROM_NOW) + '</b> <s>৳' + digitsRaw(FROM_WAS) + '</s> · প্রতিটিতে <b>' + digitsRaw(PCT) + '% ছাড়</b>',
    'hero.lead':
      'প্রতিটি সেটে কামিজ, ওড়না আর সালোয়ারের কাপড়। চেকআউটে ছাড় নিজে থেকেই বসে যায় — কোনো কোড লিখতে হবে না।',
    'hero.cta1': 'অফার দেখুন',
    'hero.cta2': 'কীভাবে কাজ করে',
    'hero.s1k': 'স্টকে ডিজাইন',
    'hero.s2k': 'আপনি সেভ করছেন',
    'hero.s3k': 'ডেলিভারি',
    'hero.s3v': 'ক্যাশ অন ডেলিভারি',

    'offer.label': 'অফার',
    'offer.head': 'একটাই ছাড়। সব ডিজাইনে। কোনো কোড লাগবে না।',
    'offer.s1h': 'পছন্দের সেট বেছে নিন',
    'offer.s1p':
      'এই পেজের প্রতিটি থ্রি পিস অফারের ভেতরে — প্রিন্টেড লন থেকে এমব্রয়ডারি করা সেট, সবই। যতগুলো চান নিন।',
    'offer.s2h': digitsRaw(PCT) + '% ছাড় নিজে থেকেই',
    'offer.s2p':
      'এই পেজ থেকে চেকআউট করলে ছাড়টি আপনার অর্ডারে যুক্ত হয়ে যায়। কনফার্ম করার আগে প্রতিটি আইটেমে সেটি দেখতে পাবেন।',
    'offer.s3h': 'পণ্য হাতে পেয়ে টাকা দিন',
    'offer.s3p':
      'সারা বাংলাদেশে ক্যাশ অন ডেলিভারি। ঢাকার ভেতরে ৳' + digitsRaw(DELIVERY.inside) + ', ঢাকার বাইরে ৳' + digitsRaw(DELIVERY.outside) + '।',
    'offer.note':
      'এই দাম শুধু এই পেজেই পাওয়া যাচ্ছে। স্টোরের অন্য যেকোনো জায়গায় এই সেটগুলোর দাম পুরোটাই।',

    'grid.label': 'কালেকশন',
    'grid.head': 'এখানের প্রতিটিই এই মুহূর্তে স্টকে আছে',
    'grid.meta': '<b>' + digitsRaw(TOTAL) + '</b>টি ডিজাইন, সবই স্টকে — প্রতিটি পেজ এখানেই দেখুন',
    'grid.fAll': 'সব',
    'grid.f1': '৳১,৫০০-এর নিচে',
    'grid.f2': '৳১,৫০০–২,৫০০',
    'grid.f3': '৳২,৫০০+',
    'grid.sort': 'দাম: কম থেকে বেশি',
    'grid.none': 'এই দামের মধ্যে এখন কিছু নেই — অন্য ফিল্টার দেখুন।',

    'bag.label': 'আপনার অর্ডার',
    'bag.head': 'আপনি যা যা বেছেছেন',
    'bag.empty': 'এখনো কিছু বাছা হয়নি। উপরের যেকোনো ডিজাইনে ট্যাপ করুন।',
    'bag.area': 'ডেলিভারি এলাকা',
    'bag.inside': 'ঢাকার ভেতরে · ৳' + digitsRaw(DELIVERY.inside),
    'bag.outside': 'ঢাকার বাইরে · ৳' + digitsRaw(DELIVERY.outside),
    'bag.sub': 'সাবটোটাল',
    'bag.save': 'ছাড় (' + digitsRaw(PCT) + '%)',
    'bag.ship': 'ডেলিভারি চার্জ',
    'bag.total': 'সর্বমোট',
    'bag.cta': 'চেকআউট করুন',

    'trust.1': 'সারা বাংলাদেশে ক্যাশ অন ডেলিভারি',
    'trust.2': 'টাকা দেওয়ার আগেই প্রতিটি আইটেমে ছাড় দেখতে পাবেন',
    'trust.3': 'বেশিরভাগ ডিজাইন মাত্র একটি — শেষ হলে আর পাওয়া যাবে না',

    'what.label': 'যা পাবেন',
    'what.head': 'প্রতিটি সেটে তিনটি অংশ',
    'what.1h': 'কামিজের কাপড়',
    'what.1p': 'মূল অংশ — আপনার বেছে নেওয়া ডিজাইন অনুযায়ী প্রিন্টেড বা এমব্রয়ডারি করা।',
    'what.2h': 'ওড়না',
    'what.2p': 'এই পেজের প্রতিটি সেটেই ওড়না রয়েছে, ব্যতিক্রম নেই।',
    'what.3h': 'সালোয়ারের কাপড়',
    'what.3p': 'কামিজের সঙ্গে মিলিয়ে নিচের অংশের কাপড়, সাধারণত সলিড ডাইড।',
    'what.4h': 'আনস্টিচড',
    'what.4p':
      'এগুলো আনস্টিচড সেট, তাই আপনার দর্জি আপনার মাপে ঠিকঠাক বানিয়ে দিতে পারবেন। সেলাই করা সেট আছে আমাদের Ready Three Piece কালেকশনে।',

    'size.label': 'সাইজ',
    'size.head': 'আনস্টিচড মানে সাইজ আপনার দর্জির হাতে',
    'size.p1':
      'এই সেটগুলো তৈরি পোশাক নয়, কাপড় হিসেবে আসে — তাই আপনাকে কোনো সাইজ বাছতে হবে না। আপনার দর্জি আপনার নিজের মাপে কাটবেন, আর সেজন্যই আনস্টিচড সেট রেডিমেডের চেয়ে ভালো ফিট হয়।',
    'size.p2': 'কোনো ডিজাইনে সাইজ বা রঙের অপশন থাকলে সেই ডিজাইনে ট্যাপ করলেই দেখতে পাবেন।',

    'faq.label': 'প্রশ্ন',
    'faq.head': 'অর্ডারের আগে',
    'faq.q1': 'কোনো কুপন কোড লাগবে?',
    'faq.a1': 'না। এই পেজ থেকে চেকআউট করলেই প্রতিটি আইটেমে ' + digitsRaw(PCT) + '% ছাড় বসে যাবে।',
    'faq.q2': 'এখানের সব ডিজাইনেই কি ছাড়?',
    'faq.a2': 'হ্যাঁ — এই পেজে দেখানো প্রতিটি থ্রি পিস অফারের ভেতরে, দাম যাই হোক।',
    'faq.q3': 'পণ্য হাতে পেয়ে টাকা দিতে পারব?',
    'faq.a3':
      'হ্যাঁ। সারা বাংলাদেশে ক্যাশ অন ডেলিভারি আছে। ঢাকার ভেতরে ডেলিভারি ৳' + digitsRaw(DELIVERY.inside) + ', ঢাকার বাইরে ৳' + digitsRaw(DELIVERY.outside) + '।',
    'faq.q4': 'আমার পছন্দের ডিজাইন শেষ হয়ে গেলে?',
    'faq.a4':
      'এখানের বেশিরভাগ ডিজাইনই মাত্র একটি করে, তাই এই পেজে শুধু সত্যিকারের স্টকে থাকা জিনিসই দেখানো হয়। কিছু হারিয়ে গেলে বুঝবেন সেটি বিক্রি হয়ে গেছে।',
    'faq.q5': 'এগুলো কি সেলাই করা নাকি আনস্টিচড?',
    'faq.a5':
      'আনস্টিচড — কামিজ, ওড়না আর সালোয়ারের কাপড় পাবেন, দর্জি দিয়ে বানিয়ে নিতে হবে। রেডিমেডের জন্য দেখুন আমাদের Ready Three Piece কালেকশন।',
    'faq.q6': 'ডেলিভারিতে কত সময় লাগে?',
    'faq.a6': 'সাধারণত ঢাকার ভেতরে ১–৩ দিন, ঢাকার বাইরে ২–৫ দিন।',

    'close.t1': 'ফ্ল্যাট ' + digitsRaw(PCT) + '% ছাড়,',
    'close.t2': 'শুধু এই পেজ থেকে।',
    'close.p': digitsRaw(TOTAL) + 'টি ডিজাইন স্টকে। বেশিরভাগই মাত্র একটি করে।',
    'close.cta': 'অফার দেখুন',

    'foot.h1': 'Tokyo Lifestyle',
    'foot.p1': 'সারা বাংলাদেশে এথনিক ও প্রতিদিনের পোশাক পৌঁছে দিই।',
    'foot.h2': 'ডেলিভারি',
    'foot.p2':
      'ঢাকার ভেতরে ৳' + digitsRaw(DELIVERY.inside) + ' · বাইরে ৳' + digitsRaw(DELIVERY.outside) + ' · ক্যাশ অন ডেলিভারি',
    'foot.h3': 'সহায়তা',
    'foot.all': 'পুরো কালেকশন',

    'dock.k': 'সর্বমোট',
    'dock.cta': 'অফার দেখুন',

    'det.name': 'পুরো নাম',
    'det.namePh': 'আপনার নাম',
    'det.phone': 'মোবাইল নম্বর',
    'det.phonePh': '০১XXXXXXXXX',
    'det.addr': 'ঠিকানা',
    'det.addrPh': 'বাসা, রোড, এলাকা',
    'det.city': 'শহর / জেলা',
    'det.cityPh': 'ঢাকা',
    'det.note':
      'এরপর Shopify-এর সিকিউর চেকআউট, আপনার তথ্য আগেই বসানো থাকবে — ক্যাশ অন ডেলিভারি, SSLCommerz বা বিকাশ বেছে কনফার্ম করুন।',

    'card.add': 'যোগ করুন',
    'card.last1': 'মাত্র ১টি বাকি',
    'card.last2': 'মাত্র কয়েকটি বাকি',

    'js.added': 'ব্যাগে যোগ হয়েছে',
    'js.removed': 'সরানো হয়েছে',
    'js.sold': 'এইমাত্র এটি বিক্রি হয়ে গেছে',
    'js.max': 'এই ডিজাইনের আর স্টক নেই',
    'js.pickOne': 'এগোতে একটি অপশন বেছে নিন',
    'js.reviewCta': 'অর্ডার দেখে নিন',
    'js.checkoutCta': 'কনফার্ম করে চেকআউট',
    'js.needName': 'আপনার নাম লিখুন',
    'js.needPhone': 'সঠিক ১১ সংখ্যার মোবাইল নম্বর লিখুন',
    'js.needAddr': 'আপনার ঠিকানা লিখুন',
    'js.needCity': 'আপনার শহর বা জেলা লিখুন',
    'js.going': 'চেকআউটে নিয়ে যাচ্ছি…',
    'js.nItems': '{n}টি আইটেম',
    'js.oneItem': '১টি আইটেম',
    'js.youSave': 'সেভ ৳{n}',
    'js.onlyN': 'মাত্র {n}টি বাকি',
    'js.emptyBag': 'আপনার ব্যাগ খালি',
    'js.remove': 'সরান',
    'js.loading': 'লোড হচ্ছে…',
    'js.qvFail': 'এই ডিজাইনটি লোড করা গেল না — এর পূর্ণ পেজ দেখুন',
    'js.fullPage': 'পণ্যের পূর্ণ পেজ দেখুন',
    'js.addToBag': 'ব্যাগে যোগ করুন · {p}',
  };

  var EN = {};
  var lang = 'en';

  function T(key) {
    if (lang === 'bn' && BN[key] != null) return BN[key];
    return EN[key] != null ? EN[key] : '';
  }

  function readEnglish() {
    var nodes = root.querySelectorAll('[data-i18n], [data-i18n-html], [data-i18n-ph], [data-i18n-aria]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var k;
      k = n.getAttribute('data-i18n');
      if (k && EN[k] == null) EN[k] = n.textContent.trim();
      k = n.getAttribute('data-i18n-html');
      if (k && EN[k] == null) EN[k] = n.innerHTML.trim();
      k = n.getAttribute('data-i18n-ph');
      if (k && EN[k] == null) EN[k] = n.getAttribute('placeholder') || '';
      k = n.getAttribute('data-i18n-aria');
      if (k && EN[k] == null) EN[k] = n.getAttribute('aria-label') || '';
    }
  }

  function applyLang(next) {
    lang = next === 'bn' ? 'bn' : 'en';
    document.documentElement.setAttribute('data-lang', lang);

    var nodes = root.querySelectorAll('[data-i18n], [data-i18n-html], [data-i18n-ph], [data-i18n-aria]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var k;
      k = n.getAttribute('data-i18n');
      if (k && T(k)) n.textContent = T(k);
      k = n.getAttribute('data-i18n-html');
      if (k && T(k)) n.innerHTML = T(k);
      k = n.getAttribute('data-i18n-ph');
      if (k && T(k)) n.setAttribute('placeholder', T(k));
      k = n.getAttribute('data-i18n-aria');
      if (k && T(k)) n.setAttribute('aria-label', T(k));
    }

    var btns = root.querySelectorAll('[data-lang-btn]');
    for (var b = 0; b < btns.length; b++) {
      btns[b].setAttribute('aria-pressed', btns[b].getAttribute('data-lang-btn') === lang ? 'true' : 'false');
    }
    render();
  }

  function setLang(next) {
    applyLang(next);
    try {
      localStorage.setItem('tll-lang', lang);
    } catch (e) {}
  }

  /* ── Numerals ────────────────────────────────────────────────────────
     BN_D and digitsRaw live above the BN dictionary — see the note there. */
  function digits(v) {
    var s = String(v);
    if (lang !== 'bn') return s;
    return s.replace(/[0-9]/g, function (d) {
      return BN_D[+d];
    });
  }

  function money(n) {
    var v = Math.round(n);
    var s = String(Math.abs(v));
    /* Bangladeshi grouping: last three, then pairs (1,23,456). */
    if (s.length > 3) {
      var head = s.slice(0, s.length - 3);
      var tail = s.slice(s.length - 3);
      head = head.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
      s = head + ',' + tail;
    }
    return (v < 0 ? '−' : '') + '৳' + digits(s);
  }


  /* ======================================================================
     3. BAG STATE
     ----------------------------------------------------------------------
     A bag line is SELF-DESCRIBING: it carries the title, image and prices it
     was created with. It used to look everything up in a product array shipped
     in the data island, which broke the moment the grid became paginated —
     page 7's cards have no entry in a page-1 island.
     ====================================================================== */
  var bag = [];   /* [{handle, id, qty, title, img, opt, p, n, max}] */
  var area = 'inside';
  var detailsOpen = false;

  function bagIndex(handle, variantId) {
    for (var i = 0; i < bag.length; i++) {
      if (bag[i].handle === handle && String(bag[i].id) === String(variantId)) return i;
    }
    return -1;
  }

  function totals() {
    var was = 0, now = 0, count = 0;
    for (var i = 0; i < bag.length; i++) {
      was += bag[i].p * bag[i].qty;
      now += bag[i].n * bag[i].qty;
      count += bag[i].qty;
    }
    var ship = count ? DELIVERY[area] : 0;
    return { count: count, was: was, now: now,
             save: Math.max(0, was - now), ship: ship, payable: now + ship };
  }

  function persist() {
    try {
      localStorage.setItem('tll-bag', JSON.stringify({ b: bag, a: area }));
    } catch (e) {}
  }

  function restore() {
    try {
      var raw = localStorage.getItem('tll-bag');
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (saved && saved.a) area = saved.a === 'outside' ? 'outside' : 'inside';
      if (!saved || !saved.b || !saved.b.length) return;
      for (var i = 0; i < saved.b.length; i++) {
        var l = saved.b[i];
        if (l && l.handle && l.id && l.qty > 0) bag.push(l);
      }
    } catch (e) {}
  }

  /* A restored bag is a claim about stock made at some earlier moment. On a
     catalogue averaging 1.27 units per design that claim is very likely stale,
     so every distinct product is re-checked against the live product JSON and
     lines that sold out are dropped rather than carried to a checkout that
     would reject them. */
  function revalidateBag() {
    if (!bag.length || !window.fetch) return;
    var handles = [];
    for (var i = 0; i < bag.length; i++) {
      if (handles.indexOf(bag[i].handle) === -1) handles.push(bag[i].handle);
    }
    var pending = handles.length;
    var dropped = 0;
    for (var h = 0; h < handles.length; h++) {
      (function (handle) {
        loadProduct(handle, function (prod) {
          if (prod) {
            for (var b = bag.length - 1; b >= 0; b--) {
              if (bag[b].handle !== handle) continue;
              var v = variantOf(prod, bag[b].id);
              if (!v || !v.available) { bag.splice(b, 1); dropped++; continue; }
              bag[b].p = Math.round(v.price / 100);
              bag[b].n = discounted(v.price);
              bag[b].max = stockOf(v);
              if (bag[b].qty > bag[b].max) bag[b].qty = bag[b].max;
            }
          }
          pending--;
          if (pending === 0) {
            persist();
            render();
            if (dropped) toast(T('js.sold'));
          }
        });
      })(handles[h]);
    }
  }

  /* ── Product fetch ──────────────────────────────────────────────────────
     /products/<handle>.js is the standard Shopify product JSON: description,
     every image, and every variant with a CENTS price. Cached per handle. */
  function loadProduct(handle, cb) {
    if (CACHE[handle]) { cb(CACHE[handle]); return; }
    if (!window.fetch) { cb(null); return; }
    fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d) CACHE[handle] = d; cb(d); })
      .catch(function () { cb(null); });
  }

  function variantOf(prod, id) {
    if (!prod) return null;
    for (var i = 0; i < prod.variants.length; i++) {
      if (String(prod.variants[i].id) === String(id)) return prod.variants[i];
    }
    return null;
  }

  /* Shopify's product JSON does not expose inventory_quantity to the
     storefront, so real depth is unknowable here. `available` is the only
     truth we get; treat a buyable variant as having a small working depth
     rather than inventing a number to show the shopper. */
  function stockOf(v) {
    if (!v || !v.available) return 0;
    return 5;
  }

  /* ======================================================================
     4. ELEMENT REFS
     ====================================================================== */
  var el = {
    grid: root.querySelector('[data-grid]'),
    gridEmpty: root.querySelector('[data-grid-empty]'),
    lines: root.querySelector('[data-lines]'),
    linesEmpty: root.querySelector('[data-lines-empty]'),
    sumSub: root.querySelector('[data-sum-sub]'),
    sumSave: root.querySelector('[data-sum-save]'),
    sumShip: root.querySelector('[data-sum-ship]'),
    sumTotal: root.querySelector('[data-sum-total]'),
    cta: root.querySelector('[data-cta]'),
    ctaLabel: root.querySelector('[data-cta-label]'),
    msg: root.querySelector('[data-msg]'),
    details: root.querySelector('[data-details]'),
    dock: root.querySelector('[data-dock]'),
    dockV: root.querySelector('[data-dock-v]'),
    dockWas: root.querySelector('[data-dock-was]'),
    dockCta: root.querySelector('[data-dock-cta]'),
    dockCtaLabel: root.querySelector('[data-dock-cta-label]'),
    fbag: root.querySelector('[data-fbag]'),
    fbagK: root.querySelector('[data-fbag-k]'),
    fbagV: root.querySelector('[data-fbag-v]'),
    fbagSave: root.querySelector('[data-fbag-save]'),
    navbag: root.querySelector('[data-navbag]'),
    navbagN: root.querySelector('[data-navbag-n]'),
    pick: root.querySelector('[data-pick]'),
    pickTitle: root.querySelector('[data-pick-title]'),
    pickBody: root.querySelector('[data-pick-body]'),
    toast: root.querySelector('[data-toast]'),
  };

  /* ======================================================================
     5. RENDER
     ====================================================================== */
  function render() {
    var t = totals();
    renderLines(t);
    renderSummary(t);
    renderDock(t);
    renderFbag(t);
    renderPickedFlags();
  }

  function renderPickedFlags() {
    var cards = root.querySelectorAll('[data-card]');
    for (var i = 0; i < cards.length; i++) {
      var h = cards[i].getAttribute('data-handle');
      var got = false;
      for (var b = 0; b < bag.length; b++) {
        if (bag[b].handle === h) got = true;
      }
      cards[i].setAttribute('data-picked', got ? 'true' : 'false');
    }
  }

  function renderLines(t) {
    if (!el.lines) return;
    if (!bag.length) {
      el.lines.innerHTML = '';
      if (el.linesEmpty) el.linesEmpty.hidden = false;
      return;
    }
    if (el.linesEmpty) el.linesEmpty.hidden = true;

    var html = '';
    for (var i = 0; i < bag.length; i++) {
      var l = bag[i];
      var showOpt = l.opt && l.opt !== 'Default Title';
      html +=
        '<div class="tll-line" data-line="' + i + '">' +
        '<img class="tll-line__img" src="' + esc(l.img) + '" alt="" loading="lazy" decoding="async">' +
        '<div>' +
        '<div class="tll-line__t">' + esc(l.title) + '</div>' +
        (showOpt ? '<div class="tll-line__v">' + esc(l.opt) + '</div>' : '') +
        '<div class="tll-line__p"><b class="tll-num">' + money(l.n * l.qty) + '</b>' +
        '<s class="tll-num">' + money(l.p * l.qty) + '</s></div>' +
        '</div>' +
        '<div class="tll-line__r">' +
        '<div class="tll-qty">' +
        '<button type="button" data-qty="-1" data-line-i="' + i + '" aria-label="Decrease">−</button>' +
        '<span>' + digits(l.qty) + '</span>' +
        '<button type="button" data-qty="1" data-line-i="' + i + '" aria-label="Increase">+</button>' +
        '</div>' +
        '<button type="button" class="tll-line__x" data-remove="' + i + '">' + esc(T('js.remove')) + '</button>' +
        '</div>' +
        '</div>';
    }
    el.lines.innerHTML = html;
  }

  function renderSummary(t) {
    if (el.sumSub) el.sumSub.textContent = money(t.was);
    if (el.sumSave) el.sumSave.textContent = '−' + money(t.save);
    if (el.sumShip) el.sumShip.textContent = money(t.ship);
    if (el.sumTotal) el.sumTotal.textContent = money(t.payable);

    if (el.ctaLabel) {
      el.ctaLabel.textContent = detailsOpen ? T('js.checkoutCta') : T('bag.cta');
    }
    if (el.cta) el.cta.disabled = t.count === 0;
  }

  function renderDock(t) {
    if (!el.dock) return;
    if (el.dockV) el.dockV.textContent = money(t.payable);
    if (el.dockWas) {
      if (t.save > 0) {
        el.dockWas.hidden = false;
        el.dockWas.textContent = money(t.was + t.ship);
      } else {
        el.dockWas.hidden = true;
      }
    }
    if (el.dockCtaLabel) {
      el.dockCtaLabel.textContent = t.count ? T('bag.cta') : T('dock.cta');
    }
    if (el.navbag) {
      el.navbag.setAttribute('data-count', String(t.count));
      if (el.navbagN) el.navbagN.textContent = digits(t.count);
    }
  }

  function renderFbag(t) {
    if (!el.fbag) return;
    el.fbag.setAttribute('data-state', t.count ? 'full' : 'empty');
    if (el.fbagK) {
      el.fbagK.textContent = t.count === 1 ? T('js.oneItem') : T('js.nItems').replace('{n}', digits(t.count));
    }
    if (el.fbagV) el.fbagV.textContent = money(t.payable);
    if (el.fbagSave) {
      if (t.save > 0) {
        el.fbagSave.hidden = false;
        el.fbagSave.textContent = T('js.youSave').replace('{n}', digits(Math.round(t.save)));
      } else {
        el.fbagSave.hidden = true;
      }
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ======================================================================
     6. ADD / REMOVE
     ====================================================================== */
  function addLine(line) {
    var idx = bagIndex(line.handle, line.id);
    var have = idx === -1 ? 0 : bag[idx].qty;
    if (have >= line.max) {
      toast(T('js.max'));
      return false;
    }
    if (idx === -1) bag.push(line);
    else bag[idx].qty += 1;

    persist();
    render();
    bumpBag();
    toast(T('js.added'));
    return true;
  }

  function changeQty(i, delta) {
    if (!bag[i]) return;
    var next = bag[i].qty + delta;
    if (next < 1) {
      bag.splice(i, 1);
      toast(T('js.removed'));
    } else if (next > bag[i].max) {
      toast(T('js.max'));
      return;
    } else {
      bag[i].qty = next;
    }
    persist();
    render();
  }

  function removeLine(i) {
    if (!bag[i]) return;
    bag.splice(i, 1);
    persist();
    render();
    toast(T('js.removed'));
  }

  function bumpBag() {
    if (!el.navbag || reduceMotion) return;
    el.navbag.setAttribute('data-bump', 'true');
    window.setTimeout(function () {
      el.navbag.setAttribute('data-bump', 'false');
    }, 200);
  }

  var toastTimer = null;
  function toast(msg) {
    if (!el.toast || !msg) return;
    el.toast.textContent = msg;
    el.toast.setAttribute('data-show', 'true');
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      el.toast.setAttribute('data-show', 'false');
    }, 2200);
  }

  function setMsg(text, isErr) {
    if (!el.msg) return;
    el.msg.textContent = text || '';
    el.msg.setAttribute('data-err', isErr ? 'true' : 'false');
  }

  /* ======================================================================
     7. QUICK VIEW
     ----------------------------------------------------------------------
     The whole point of this page is that a shopper never has to leave it, so
     tapping a card opens the product HERE — every photo, the real description,
     the variant choice and an add-to-bag button — instead of navigating to the
     main store.

     Data comes from /products/<handle>.js on demand. Cards stay real <a> links
     underneath, so crawlers, middle-click and a JS failure all still work.
     ====================================================================== */
  var qvHandle = null;
  var qvVariant = null;
  var qvImg = 0;

  function openQuick(handle, card) {
    qvHandle = handle;
    qvVariant = null;
    qvImg = 0;

    /* Paint the skeleton immediately from what the card already knows. A
       spinner-only dialog on a slow Bangladeshi mobile connection reads as a
       broken tap; showing the title and price straight away does not. */
    var title = card ? card.getAttribute('data-title') : '';
    var now = card ? parseFloat(card.getAttribute('data-price')) : 0;
    var was = card ? parseFloat(card.getAttribute('data-was')) : 0;
    var thumb = card ? card.querySelector('img') : null;

    if (el.pickTitle) el.pickTitle.textContent = title || '';
    if (el.pickBody) {
      el.pickBody.innerHTML =
        '<div class="tll-qv" data-qv>' +
        '<div class="tll-qv__gal">' +
        '<div class="tll-qv__stage">' +
        (thumb ? '<img src="' + esc(thumb.getAttribute('src')) + '" alt="" data-qv-main>' : '') +
        '</div>' +
        '<div class="tll-qv__thumbs" data-qv-thumbs></div>' +
        '</div>' +
        '<div class="tll-qv__info">' +
        '<div class="tll-qv__price">' +
        '<b class="tll-num">' + money(now) + '</b>' +
        '<s class="tll-num">' + money(was) + '</s>' +
        '<span class="tll-qv__off">−' + digits(PCT) + '%</span>' +
        '</div>' +
        '<div class="tll-qv__opts" data-qv-opts></div>' +
        '<button type="button" class="tll-btn tll-btn--rose tll-btn--block tll-qv__add" data-qv-add disabled>' +
        esc(T('js.loading')) + '</button>' +
        '<div class="tll-qv__desc" data-qv-desc></div>' +
        '<a class="tll-qv__full" href="/products/' + esc(handle) + '" data-qv-full>' +
        esc(T('js.fullPage')) + '</a>' +
        '</div></div>';
    }
    if (el.pick) el.pick.setAttribute('data-open', 'true');
    lockScroll(true);

    loadProduct(handle, function (prod) {
      if (qvHandle !== handle) return;          /* shopper moved on already */
      if (!prod) {
        var add = root.querySelector('[data-qv-add]');
        if (add) { add.textContent = T('js.qvFail'); add.disabled = true; }
        return;
      }
      paintQuick(prod);
    });
  }

  function paintQuick(prod) {
    if (el.pickTitle) el.pickTitle.textContent = prod.title;

    /* Gallery */
    var imgs = prod.images && prod.images.length ? prod.images : [prod.featured_image];
    var thumbs = root.querySelector('[data-qv-thumbs]');
    if (thumbs) {
      if (imgs.length > 1) {
        var th = '';
        for (var i = 0; i < imgs.length; i++) {
          th += '<button type="button" class="tll-qv__thumb" data-qv-img="' + i + '"' +
                (i === 0 ? ' aria-current="true"' : '') + '>' +
                '<img src="' + esc(sized(imgs[i], 160)) + '" alt="" loading="lazy" decoding="async"></button>';
        }
        thumbs.innerHTML = th;
      } else {
        thumbs.innerHTML = '';
      }
    }
    setQuickImage(0);

    /* Variants. A single Default Title variant is not a choice — do not make
       the shopper tap it. */
    var opts = root.querySelector('[data-qv-opts]');
    var real = [];
    for (var v = 0; v < prod.variants.length; v++) {
      if (prod.variants[v].available) real.push(prod.variants[v]);
    }
    if (!real.length) {
      if (opts) opts.innerHTML = '<p class="tll-qv__sold">' + esc(T('js.sold')) + '</p>';
      var addSold = root.querySelector('[data-qv-add]');
      if (addSold) { addSold.textContent = T('js.sold'); addSold.disabled = true; }
      return;
    }

    var single = real.length === 1 && real[0].title === 'Default Title';
    if (opts) {
      if (single) {
        opts.innerHTML = '';
      } else {
        var h = '<span class="tll-qv__optk">' +
                esc(prod.options && prod.options[0] ? prod.options[0].name || prod.options[0] : 'Option') +
                '</span><div class="tll-pick__opts">';
        for (var r = 0; r < prod.variants.length; r++) {
          var vv = prod.variants[r];
          h += '<button type="button" class="tll-opt" data-qv-variant="' + vv.id + '"' +
               (vv.available ? '' : ' disabled') + '>' + esc(vv.title) + '</button>';
        }
        h += '</div>';
        opts.innerHTML = h;
      }
    }
    selectVariant(single ? real[0].id : (real.length === 1 ? real[0].id : null));

    /* Description — Shopify returns sanitised HTML the merchant wrote. */
    var desc = root.querySelector('[data-qv-desc]');
    if (desc) desc.innerHTML = prod.description || '';
  }

  function sized(img, w) {
    var src = typeof img === 'string' ? img : (img && img.src) || '';
    if (!src) return '';
    src = src.split('?')[0];
    return src + '?width=' + w;
  }

  function setQuickImage(i) {
    var prod = CACHE[qvHandle];
    if (!prod) return;
    var imgs = prod.images && prod.images.length ? prod.images : [prod.featured_image];
    if (i < 0 || i >= imgs.length) return;
    qvImg = i;
    var main = root.querySelector('[data-qv-main]');
    if (main) main.setAttribute('src', sized(imgs[i], 900));
    var ts = root.querySelectorAll('[data-qv-img]');
    for (var t = 0; t < ts.length; t++) {
      ts[t].setAttribute('aria-current', Number(ts[t].getAttribute('data-qv-img')) === i ? 'true' : 'false');
    }
  }

  function selectVariant(id) {
    var prod = CACHE[qvHandle];
    var add = root.querySelector('[data-qv-add]');
    qvVariant = id ? String(id) : null;

    var btns = root.querySelectorAll('[data-qv-variant]');
    for (var b = 0; b < btns.length; b++) {
      btns[b].setAttribute('aria-pressed',
        qvVariant && btns[b].getAttribute('data-qv-variant') === qvVariant ? 'true' : 'false');
    }
    if (!add) return;

    if (!qvVariant) {
      add.textContent = T('js.pickOne');
      add.disabled = true;
      return;
    }
    var v = variantOf(prod, qvVariant);
    if (!v || !v.available) {
      add.textContent = T('js.sold');
      add.disabled = true;
      return;
    }
    add.textContent = T('js.addToBag').replace('{p}', money(discounted(v.price)));
    add.disabled = false;

    /* Reflect the chosen variant's own price — variants of one design can
       differ, and the header price came from the card's default. */
    var pr = root.querySelector('.tll-qv__price');
    if (pr) {
      pr.innerHTML =
        '<b class="tll-num">' + money(discounted(v.price)) + '</b>' +
        '<s class="tll-num">' + money(Math.round(v.price / 100)) + '</s>' +
        '<span class="tll-qv__off">−' + digits(PCT) + '%</span>';
    }
    if (v.featured_image) {
      var imgs = (prod.images || []);
      for (var i = 0; i < imgs.length; i++) {
        if (sized(imgs[i], 1) === sized(v.featured_image.src || v.featured_image, 1)) {
          setQuickImage(i);
          break;
        }
      }
    }
  }

  function addFromQuick() {
    var prod = CACHE[qvHandle];
    var v = variantOf(prod, qvVariant);
    if (!prod || !v || !v.available) { toast(T('js.sold')); return; }
    var imgs = prod.images && prod.images.length ? prod.images : [prod.featured_image];
    var ok = addLine({
      handle: prod.handle,
      id: v.id,
      qty: 1,
      title: prod.title,
      img: sized(imgs[0], 200),
      opt: v.title,
      p: Math.round(v.price / 100),
      n: discounted(v.price),
      max: stockOf(v),
    });
    if (ok) closeQuick();
  }

  function closeQuick() {
    qvHandle = null;
    qvVariant = null;
    if (el.pick) el.pick.setAttribute('data-open', 'false');
    lockScroll(false);
  }

  /* iOS Safari ignores overflow:hidden on <html> alone, so both must be set. */
  function lockScroll(on) {
    var v = on ? 'hidden' : '';
    document.documentElement.style.overflow = v;
    document.body.style.overflow = v;
  }

  /* ======================================================================
     8. CHECKOUT
     ----------------------------------------------------------------------
     A theme cannot create an order, so the hand-off is a cart permalink:
       /cart/{variant}:{qty},...?discount=CODE&checkout[...]=...
     The permalink REPLACES the cart rather than adding to it, which rules out
     a stale cart contaminating the total — and, critically, it carries the
     discount code explicitly so the 10% does not depend on a session cookie
     having survived the trip from the ad.
     ====================================================================== */
  function aggregate() {
    var out = [];
    for (var i = 0; i < bag.length; i++) {
      var found = -1;
      for (var j = 0; j < out.length; j++) {
        if (String(out[j].id) === String(bag[i].id)) found = j;
      }
      if (found === -1) out.push({ id: bag[i].id, quantity: bag[i].qty });
      else out[found].quantity += bag[i].qty;
    }
    return out;
  }

  function normalizeBdPhone(raw) {
    var d = String(raw || '').replace(/[^\d]/g, '');
    if (d.indexOf('880') === 0) d = d.slice(3);
    if (d.length === 11 && d.indexOf('01') === 0) return d;
    if (d.length === 10 && d.indexOf('1') === 0) return '0' + d;
    return '';
  }

  function detField(name) {
    return root.querySelector('[data-det="' + name + '"]');
  }

  function setFieldError(name, on) {
    var f = detField(name);
    if (f) f.setAttribute('data-err', on ? 'true' : 'false');
  }

  function validateDetails() {
    var out = { ok: true };
    var names = ['name', 'phone', 'addr', 'city'];
    for (var i = 0; i < names.length; i++) setFieldError(names[i], false);

    var name = detField('name') ? detField('name').value.trim() : '';
    var phone = detField('phone') ? detField('phone').value.trim() : '';
    var addr = detField('addr') ? detField('addr').value.trim() : '';
    var city = detField('city') ? detField('city').value.trim() : '';

    if (!name) {
      setFieldError('name', true);
      setMsg(T('js.needName'), true);
      return { ok: false };
    }
    var np = normalizeBdPhone(phone);
    if (!np || !/^01[3-9][0-9]{8}$/.test(np)) {
      setFieldError('phone', true);
      setMsg(T('js.needPhone'), true);
      return { ok: false };
    }
    if (!addr) {
      setFieldError('addr', true);
      setMsg(T('js.needAddr'), true);
      return { ok: false };
    }
    if (!city) {
      setFieldError('city', true);
      setMsg(T('js.needCity'), true);
      return { ok: false };
    }
    out.name = name;
    out.phone = np;
    out.addr = addr;
    out.city = city;
    return out;
  }

  function checkoutPermalink(d) {
    var items = aggregate();
    if (!items.length) return '';
    var parts = [];
    for (var i = 0; i < items.length; i++) parts.push(items[i].id + ':' + items[i].quantity);

    var q = [];
    function add(k, v) {
      if (v) q.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    }

    /* The discount code first, because it is the one parameter this whole page
       depends on. Everything after it is convenience. */
    add('discount', CODE);

    var bits = String(d.name || '').split(' ');
    var sp = 'checkout[shipping_address]';
    add(sp + '[first_name]', bits[0] || d.name);
    add(sp + '[last_name]', bits.slice(1).join(' ') || '.');
    add(sp + '[address1]', d.addr);
    add(sp + '[city]', d.city);
    add(sp + '[country]', 'Bangladesh');
    add(sp + '[phone]', d.phone);
    /* Carried onto the order so packing slips show where it came from. */
    add('attributes[Source]', 'Three Piece landing page');
    add('attributes[Delivery area]', area === 'outside' ? 'Outside Dhaka' : 'Inside Dhaka');

    return '/cart/' + parts.join(',') + (q.length ? '?' + q.join('&') : '');
  }

  function checkout() {
    var t = totals();
    if (!t.count) {
      jumpTo('collection');
      return;
    }

    /* Press one reveals the fields; press two validates and leaves. */
    if (el.details && !detailsOpen) {
      detailsOpen = true;
      el.details.hidden = false;
      setMsg('');
      renderSummary(t);
      var first = detField('name');
      if (first) {
        try {
          first.focus({ preventScroll: true });
        } catch (e) {
          first.focus();
        }
      }
      return;
    }

    var d = validateDetails();
    if (!d.ok) return;

    setMsg(T('js.going'), false);
    var url = checkoutPermalink(d);
    if (url) window.location.href = url;
  }

  /* ======================================================================
     9. GRID FILTER + SORT
     ====================================================================== */
  var filterMode = 'all';
  var sortAsc = false;

  function priceBand(price) {
    if (price < 1500) return 'under1500';
    if (price <= 2500) return '1500to2500';
    return 'over2500';
  }

  function applyGrid() {
    if (!el.grid) return;

    flip(el.grid, function () {
      var cards = el.grid.querySelectorAll('[data-card]');
      var shown = 0;
      var i;

      for (i = 0; i < cards.length; i++) {
        var price = parseFloat(cards[i].getAttribute('data-price')) || 0;
        var keep = filterMode === 'all' || priceBand(price) === filterMode;
        cards[i].setAttribute('data-hidden', keep ? 'false' : 'true');
        if (keep) shown++;
      }

      if (sortAsc) {
        var list = [];
        for (i = 0; i < cards.length; i++) list.push(cards[i]);
        list.sort(function (a, b) {
          return (
            (parseFloat(a.getAttribute('data-price')) || 0) -
            (parseFloat(b.getAttribute('data-price')) || 0)
          );
        });
        for (i = 0; i < list.length; i++) el.grid.appendChild(list[i]);
      }

      if (el.gridEmpty) el.gridEmpty.hidden = shown !== 0;
    });
  }

  /* ======================================================================
     9b. PAGINATION
     ----------------------------------------------------------------------
     The pager links are real hrefs, so with JS off (or if this fails) they
     simply navigate and the page still works. With JS they are intercepted and
     the grid is swapped in place through Shopify's Section Rendering API,
     which re-renders just this section rather than the whole document.
     ====================================================================== */
  var paging = false;

  function gotoPage(href) {
    if (!href || paging || !window.fetch) {
      if (href) window.location.href = href;
      return;
    }
    paging = true;
    if (el.grid) el.grid.setAttribute('data-loading', 'true');

    /* section_id returns ONLY this section's markup. Strip any hash first —
       Shopify treats it as part of the query otherwise. */
    var clean = href.split('#')[0];
    var url = clean + (clean.indexOf('?') === -1 ? '?' : '&') +
              'section_id=' + encodeURIComponent(SECTION_ID);

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) throw new Error('no html');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshGrid = doc.querySelector('[data-grid]');
        var freshPager = doc.querySelector('[data-pager]');
        var freshMeta = doc.querySelector('[data-pagemeta]');
        if (!freshGrid) throw new Error('no grid in response');

        if (el.grid) el.grid.innerHTML = freshGrid.innerHTML;
        if (el.grid) el.grid.setAttribute('data-page', freshGrid.getAttribute('data-page') || '');

        var pager = root.querySelector('[data-pager]');
        if (pager && freshPager) pager.innerHTML = freshPager.innerHTML;
        var meta = root.querySelector('[data-pagemeta]');
        if (meta && freshMeta) meta.textContent = freshMeta.textContent;

        /* Keep the address bar honest so the page is shareable and the back
           button returns to the page the shopper was actually looking at. */
        try { window.history.pushState({}, '', clean + '#collection'); } catch (e) {}

        afterGridSwap();
      })
      .catch(function () {
        window.location.href = href;      /* fall back to a real navigation */
      })
      .then(function () {
        paging = false;
        if (el.grid) el.grid.setAttribute('data-loading', 'false');
      });
  }

  /* Newly-injected cards have never been through the reveal observer, are not
     translated, and do not know what is already in the bag. */
  function afterGridSwap() {
    var cards = el.grid ? el.grid.querySelectorAll('[data-rv]') : [];
    for (var i = 0; i < cards.length; i++) cards[i].setAttribute('data-in', 'true');
    applyLang(lang);
    applyGrid();
    var sec = document.getElementById('collection');
    if (sec) sec.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  window.addEventListener('popstate', function () {
    gotoPage(window.location.pathname + window.location.search);
  });

  /* ======================================================================
     10. SCROLL CHOREOGRAPHY
     ====================================================================== */
  function jumpTo(id) {
    var target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function setupScroll() {
    var nav = root.querySelector('[data-nav]');
    var spineNow = root.querySelector('[data-spine-now]');
    var sections = root.querySelectorAll('[data-sec-label]');
    var links = root.querySelectorAll('[data-nav-link]');

    function markCurrent(id) {
      var label = '';
      for (var i = 0; i < sections.length; i++) {
        if (sections[i].id === id) label = sections[i].getAttribute('data-sec-label');
      }
      if (spineNow && label) spineNow.textContent = label;
      for (var j = 0; j < links.length; j++) {
        links[j].setAttribute(
          'aria-current',
          links[j].getAttribute('data-nav-link') === id ? 'true' : 'false'
        );
      }
    }

    if ('IntersectionObserver' in window && sections.length) {
      var seen = {};
      var secObs = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            seen[entries[i].target.id] = entries[i].isIntersecting;
          }
          for (var k = sections.length - 1; k >= 0; k--) {
            if (seen[sections[k].id]) {
              markCurrent(sections[k].id);
              return;
            }
          }
        },
        { rootMargin: '-14% 0px -70% 0px' }
      );
      for (var s = 0; s < sections.length; s++) secObs.observe(sections[s]);
    }

    /* Pause the always-on animations while the hero is off screen. */
    var hero = document.getElementById('top');
    if ('IntersectionObserver' in window && hero) {
      var animObs = new IntersectionObserver(
        function (entries) {
          root.setAttribute('data-anim-paused', entries[0].isIntersecting ? 'false' : 'true');
        },
        { threshold: 0 }
      );
      animObs.observe(hero);
    }

    /* Reads pageYOffset only — no getBoundingClientRect, so this cannot force
       a synchronous layout on any scroll frame. */
    function onScroll() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      if (nav) nav.setAttribute('data-stuck', y > 12 ? 'true' : 'false');
      var show = y > 400 ? 'true' : 'false';
      if (el.dock) el.dock.setAttribute('data-show', show);
      if (el.fbag) el.fbag.setAttribute('data-show', show);
    }

    var raf = false;
    window.addEventListener(
      'scroll',
      function () {
        if (raf) return;
        raf = true;
        window.requestAnimationFrame(function () {
          raf = false;
          onScroll();
        });
      },
      { passive: true }
    );
    onScroll();
  }

  /* ======================================================================
     11. THEME
     ====================================================================== */
  function setTheme(next) {
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem('tll-theme', next);
    } catch (e) {}
  }

  function currentTheme() {
    var attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark' || attr === 'light') return attr;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  /* ======================================================================
     12. EVENTS
     ====================================================================== */
  root.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;

    var hit;

    /* Order matters: nested controls must be tested before their containers. */
    hit = t.closest('[data-qty]');
    if (hit) {
      changeQty(parseInt(hit.getAttribute('data-line-i'), 10), parseInt(hit.getAttribute('data-qty'), 10));
      return;
    }

    hit = t.closest('[data-remove]');
    if (hit) {
      removeLine(parseInt(hit.getAttribute('data-remove'), 10));
      return;
    }

    hit = t.closest('[data-qv-variant]');
    if (hit) {
      selectVariant(hit.getAttribute('data-qv-variant'));
      return;
    }

    hit = t.closest('[data-qv-img]');
    if (hit) {
      setQuickImage(Number(hit.getAttribute('data-qv-img')) || 0);
      return;
    }

    hit = t.closest('[data-qv-add]');
    if (hit) {
      addFromQuick();
      return;
    }

    /* The "open the real product page" escape hatch must NOT be intercepted. */
    if (t.closest('[data-qv-full]')) return;

    hit = t.closest('[data-pick-close]');
    if (hit) {
      closeQuick();
      return;
    }
    if (t.closest('[data-pick-veil]')) {
      closeQuick();
      return;
    }

    hit = t.closest('[data-filter]');
    if (hit) {
      filterMode = hit.getAttribute('data-filter');
      var chips = root.querySelectorAll('[data-filter]');
      for (var c = 0; c < chips.length; c++) {
        chips[c].setAttribute('aria-pressed', chips[c] === hit ? 'true' : 'false');
      }
      applyGrid();
      return;
    }

    hit = t.closest('[data-sort]');
    if (hit) {
      sortAsc = !sortAsc;
      hit.setAttribute('aria-pressed', sortAsc ? 'true' : 'false');
      applyGrid();
      return;
    }

    /* Cards are real <a href> for crawlers and no-JS. Intercept only a plain
       left click, so middle-click / cmd-click still open the product page. */
    hit = t.closest('[data-card]');
    if (hit && !ev.metaKey && !ev.ctrlKey && !ev.shiftKey && !ev.altKey && ev.button === 0) {
      ev.preventDefault();
      openQuick(hit.getAttribute('data-handle'), hit);
      return;
    }

    hit = t.closest('[data-page-link]');
    if (hit && !ev.metaKey && !ev.ctrlKey && !ev.shiftKey && !ev.altKey && ev.button === 0) {
      ev.preventDefault();
      gotoPage(hit.getAttribute('href'));
      return;
    }

    hit = t.closest('[data-lang-btn]');
    if (hit) {
      setLang(hit.getAttribute('data-lang-btn'));
      return;
    }

    hit = t.closest('[data-theme-toggle]');
    if (hit) {
      setTheme(currentTheme() === 'dark' ? 'light' : 'dark');
      return;
    }

    hit = t.closest('[data-navbag]');
    if (hit) {
      jumpTo(bag.length ? 'bag' : 'collection');
      return;
    }

    hit = t.closest('[data-fbag]');
    if (hit) {
      jumpTo('bag');
      return;
    }

    hit = t.closest('[data-cta]');
    if (hit) {
      checkout();
      return;
    }

    hit = t.closest('[data-dock-cta]');
    if (hit) {
      if (bag.length) jumpTo('bag');
      else jumpTo('collection');
      return;
    }

    hit = t.closest('[data-jump]');
    if (hit) {
      ev.preventDefault();
      jumpTo(hit.getAttribute('data-jump'));
      return;
    }
  });

  root.addEventListener('change', function (ev) {
    var t = ev.target;
    if (t && t.name === 'tll-area') {
      area = t.value === 'outside' ? 'outside' : 'inside';
      persist();
      render();
    }
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && el.pick && el.pick.getAttribute('data-open') === 'true') {
      closeQuick();
    }
  });

  /* The layout renders the chat buttons OUTSIDE #tll-root, so applyLang never
     walks them. They watch data-lang themselves — mirrored here for the theme
     meta so an OS theme change while the page is open is honoured for visitors
     who have never touched the toggle. */
  if (window.matchMedia) {
    var mq = window.matchMedia('(prefers-color-scheme: dark)');
    var onScheme = function () {
      if (!document.documentElement.getAttribute('data-theme')) render();
    };
    if (mq.addEventListener) mq.addEventListener('change', onScheme);
    else if (mq.addListener) mq.addListener(onScheme);
  }

  /* ======================================================================
     13. BOOT
     ====================================================================== */
  readEnglish();

  var savedLang = 'en';
  try {
    savedLang = localStorage.getItem('tll-lang') === 'bn' ? 'bn' : 'en';
  } catch (e) {}

  restore();

  /* Reflect the restored delivery area into the radio group before first
     render, or the summary and the checked radio disagree. */
  var areaInput = root.querySelector('input[name="tll-area"][value="' + area + '"]');
  if (areaInput) areaInput.checked = true;

  applyLang(savedLang);

  revalidateBag();

  reveal();
  parallax();
  heroRotate();
  magnetic();
  counters();
  setupScroll();
  render();
})();
